import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  Pagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import { useTableState } from '../../../hooks/useTableState';
import { useResponseHandler } from '../../../hooks/useResponseHandler';
import {
  deleteShiftDemandStore,
  restoreShiftDemandStore,
  getShiftDemandsStore,
} from '../../../store/shift_demands/thunks';
import { hasPemissions } from '../../../commons/utils';
import { EditShiftDemandModal } from './modals/EditShiftDemandModal';
import { GenericTableHead, HeadCell } from '../../../commons/generics/GenericTableHead';
import { StyledTableCell, StyledTableRow } from '../../../commons/generics/StyledTableComponents';
import { SinDatos } from '../../../commons/generics/SinDatos';
import { ModalConfirmDeactivation } from '../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../components/modals/ModalConfirmRestore';

const headCells: HeadCell[] = [
  { id: 'name', label: 'Nombre / Cargos' },
  { id: 'startDate', label: 'Fecha Inicio' },
  { id: 'endDate', label: 'Fecha Fin' },
  { id: 'actions', label: 'Acciones', align: 'center' },
];

export const TableShiftDemands = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { handleResponse, ResponseComponents } = useResponseHandler();
  const dataList = useSelector((state: any) => state.shiftDemands?.shift_demands_list) || [];
  const meta = useSelector((state: any) => state.shiftDemands?.meta);

  const [hasPermission, setHasPermission] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canRestore, setCanRestore] = useState(false);

  const [openEdit, setOpenEdit] = useState(false);
  const [selectedData, setSelectedData] = useState<any>(null);

  const {
    pagePagination,
    rowsPerPage,
    searchTerm: inputValue,
    checked,
    changePaginationPage: changePaginationPageHook,
    handleChangeRowsPerPage: changeRowsPerPageHook,
    handleSwitchChange: onSwitchChangeHook,
    setSearchTerm,
    getSortedAndFilteredList,
    order,
    orderBy,
    handleRequestSort,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    openElem,
    setOpenElem,
  } = useTableState('shiftDemands');

  const handleSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;
    setSearchTerm(search);
    const response = await dispatch(
      getShiftDemandsStore(
        1,
        rowsPerPage,
        search ? `search=${search}&status=${checked ? 0 : 1}` : `status=${checked ? 0 : 1}`
      )
    );
    await handleResponse(response, 'buscar requerimientos', false);
  };

  const onSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    onSwitchChangeHook(event);
    const isChecked = event.target.checked;
    const response = await dispatch(
      getShiftDemandsStore(
        1,
        rowsPerPage,
        inputValue
          ? `search=${inputValue}&status=${isChecked ? 0 : 1}`
          : `status=${isChecked ? 0 : 1}`
      )
    );
    await handleResponse(response, 'listar requerimientos', false);
  };

  const onResetSearch = async () => {
    setSearchTerm('');
    const response = await dispatch(
      getShiftDemandsStore(1, rowsPerPage, `status=${checked ? 0 : 1}`)
    );
    await handleResponse(response, 'restablecer búsqueda', false);
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    const response = await dispatch(
      getShiftDemandsStore(
        value,
        rowsPerPage,
        inputValue ? `search=${inputValue}&status=${checked ? 0 : 1}` : `status=${checked ? 0 : 1}`
      )
    );
    await handleResponse(response, 'cambiar página', false);
  };

  const changeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    changeRowsPerPageHook(event);
    const response = await dispatch(
      getShiftDemandsStore(
        1,
        newRowsPerPage,
        inputValue ? `search=${inputValue}&status=${checked ? 0 : 1}` : `status=${checked ? 0 : 1}`
      )
    );
    await handleResponse(response, 'cambiar registros por página', false);
  };

  const handleDelete = (row: any) => {
    setOpenElem(row);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (row: any) => {
    setOpenElem(row);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const param = inputValue
      ? `search=${inputValue}&status=${checked ? 0 : 1}`
      : `status=${checked ? 0 : 1}`;
    const response = await dispatch(
      deleteShiftDemandStore(openElem.id, pagePagination, rowsPerPage, param)
    );
    await handleResponse(response, 'desactivar requerimiento', true);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const param = inputValue
      ? `search=${inputValue}&status=${checked ? 0 : 1}`
      : `status=${checked ? 0 : 1}`;
    const response = await dispatch(
      restoreShiftDemandStore(openElem.id, pagePagination, rowsPerPage, param)
    );
    await handleResponse(response, 'restaurar requerimiento', true);
  };

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'view',
      'modulo.programador.requerimiento.de.personal'
    );
    if (!view) {
      setHasPermission(false);
    } else {
      setCanView(true);
    }

    const edit = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'edit',
      'modulo.programador.requerimiento.de.personal'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'delete',
      'modulo.programador.requerimiento.de.personal'
    );
    if (del) setCanDelete(true);

    const restore = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'restore',
      'modulo.programador.requerimiento.de.personal'
    );
    if (restore) setCanRestore(true);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      const fetchData = async () => {
        const param = inputValue
          ? `search=${inputValue}&status=${checked ? 0 : 1}`
          : `status=${checked ? 0 : 1}`;
        const response = await dispatch(getShiftDemandsStore(pagePagination, rowsPerPage, param));
        await handleResponse(response, 'obtener requerimientos de personal', false);
      };
      fetchData();
    }
  }, [canView]);

  const handleEdit = (row: any) => {
    navigate(`/talento-humano/programador/requerimiento-de-personal/${row.id}/editar`);
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">No tienes permisos para ver este contenido.</Typography>
      </Box>
    );
  }

  const sortedList = getSortedAndFilteredList(
    dataList,
    ['name', 'startDate', 'endDate'],
    'status',
    checked ? 0 : 1
  );

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar requerimiento..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={inputValue}
          onChange={handleSearch}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivos (Requerimientos)
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <GenericTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedList.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <SinDatos />
                  </StyledTableCell>
                </TableRow>
              ) : (
                sortedList.map((row: any) => (
                  <StyledTableRow key={row.id} hover>
                    <StyledTableCell>{row.name}</StyledTableCell>
                    <StyledTableCell>{row.startDate}</StyledTableCell>
                    <StyledTableCell>{row.endDate}</StyledTableCell>

                    <StyledTableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <span
                          style={{
                            cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => canEdit && row.status === 1 && handleEdit(row)}
                            disabled={!canEdit || row.status !== 1}
                            color="primary"
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>

                        {row.status === 1 ? (
                          <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canDelete && handleDelete(row)}
                              disabled={!canDelete}
                              color="error"
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        ) : (
                          <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canRestore && handleRestore(row)}
                              disabled={!canRestore}
                              color="success"
                            >
                              <RestoreOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        )}
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>

          {meta && (
            <>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={meta.itemCount || 0}
                page={pagePagination - 1}
                onPageChange={() => {}}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={changeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 1,
                  padding: 2.5,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Pagination
                  sx={{ justifyContent: 'center' }}
                  color="primary"
                  count={meta.pageCount || 1}
                  page={pagePagination}
                  onChange={changePaginationPage}
                  showFirstButton
                  showLastButton
                  shape="circular"
                />
              </Box>
            </>
          )}
        </TableContainer>
      </Card>

      <EditShiftDemandModal
        open={openEdit}
        setOpen={setOpenEdit}
        data={selectedData}
        onSuccess={() => {}}
      />

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
